import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect, { type ObjectSelectValue } from "@components/ObjectSelect";
import { pollingLogColumns } from "@features/communications";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  usePollingLog,
  usePollingLogCodes,
} from "@shared/api/generated/communications/communications";
import type { PollingLogEntry } from "@shared/api/generated/model";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, DatePicker, InputNumber, Select, Space, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";

const { RangePicker } = DatePicker;

const PollingLogPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const [selectedObject, setSelectedObject] = useState<ObjectSelectValue | undefined>();
  const [meter, setMeter] = useState<number | undefined>();
  const [code, setCode] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, "day").startOf("day"),
    dayjs(),
  ]);

  const codesQuery = usePollingLogCodes();
  const codeOptions = useMemo(
    () =>
      (codesQuery.data ?? []).map((c) => ({
        label: `${c.code} - ${c.text}`,
        value: c.code,
      })),
    [codesQuery.data],
  );

  const resetKey = [
    selectedObject?.objectId,
    selectedObject?.regionId,
    range[0].valueOf(),
    range[1].valueOf(),
    meter,
    code,
  ].join("|");

  const logQuery = usePollingLog({
    meter,
    code,
    region: selectedObject?.regionId,
    object: selectedObject?.objectId,
    from: range[0].format("YYYY-MM-DD HH:mm"),
    to: range[1].format("YYYY-MM-DD HH:mm"),
    limit: 500,
  });

  const data = useMemo(() => logQuery.data ?? [], [logQuery.data]);

  const pagination = useStandardPagination(data.length, 50, resetKey);

  return (
    <PageLayoutWithHelp title="Журнал опроса">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          События опроса счётчиков: ошибки связи, коррекция времени, системные ошибки и др.
        </Typography.Text>

        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => {
                setSelectedObject(value);
              }}
              allowClear
              placeholder="Объект (все доступные)"
              style={{ minWidth: 320 }}
            />
            <RangePicker
              value={range}
              showTime
              onChange={(value) => {
                if (value?.[0] && value[1]) {
                  setRange([value[0], value[1]]);
                }
              }}
            />
            <InputNumber
              value={meter}
              onChange={(value) => {
                setMeter(value ?? undefined);
              }}
              placeholder="ID счётчика"
              style={{ width: 140 }}
            />
            <Select
              value={code}
              onChange={(value) => {
                setCode(value);
              }}
              options={codeOptions}
              loading={codesQuery.isLoading}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Код события"
              style={{ minWidth: 280 }}
            />
          </FilterRow>
        </FilterBar>

        {!logQuery.isLoading && data.length === 0 ? (
          <Alert type="info" showIcon title="Записей не найдено" />
        ) : (
          <EnterpriseTable<PollingLogEntry>
            rowKey={(row) => `${row.id}`}
            columns={pollingLogColumns}
            dataSource={data}
            loading={logQuery.isLoading}
            pagination={pagination}
            scroll={{ x: 1000 }}
            size="small"
            toolbar={{ reload: true, onReload: () => logQuery.refetch() }}
          />
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default PollingLogPage;
